import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ChevronDown, LogOut } from "lucide-react";
import { cn } from "@/lib/utils";
import type { LucideIcon } from "lucide-react";
import { PortalSwitcherMenu } from "@/components/admin/PortalSwitcherMenu";

export interface NavProfileAction {
  label: string;
  icon: LucideIcon;
  onSelect: () => void;
}

export interface NavProfileSection {
  label?: string;
  items: NavProfileAction[];
}

interface NavProfileMenuProps {
  name?: string | null;
  email?: string | null;
  avatarUrl?: string | null;
  fallbackInitial?: string;
  primaryActions: NavProfileAction[];
  sections?: NavProfileSection[];
  roleActions?: NavProfileAction[];
  onSignOut: () => void;
  signOutLabel?: string;
  triggerClassName?: string;
  /** Show name next to avatar in trigger (hidden on small screens by default) */
  showNameInTrigger?: boolean;
}

export function NavProfileMenu({
  name,
  email,
  avatarUrl,
  fallbackInitial = "U",
  primaryActions,
  sections,
  roleActions,
  onSignOut,
  signOutLabel = "Sign Out",
  triggerClassName,
  showNameInTrigger = false,
}: NavProfileMenuProps) {
  const fallbackCharacter = fallbackInitial?.charAt(0).toUpperCase() || "U";

  return (
    <DropdownMenu modal={true} key={`profile-menu-${name || 'default'}`}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className={cn(
            "gap-2 pl-2 hover:bg-muted/70 hover:text-foreground transition-colors group",
            triggerClassName
          )}
        >
          <Avatar className="size-8">
            <AvatarImage src={avatarUrl || undefined} alt={name ?? "Profile"} />
            <AvatarFallback>{fallbackCharacter}</AvatarFallback>
          </Avatar>
          {showNameInTrigger && name && name !== "Account" && (
            <span className="hidden max-w-[120px] truncate text-sm font-medium lg:inline">
              {name}
            </span>
          )}
          <ChevronDown className="size-4 text-muted-foreground transition-colors group-hover:text-foreground" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-60" sideOffset={4}>
        <div className="border-b border-border px-3 py-2.5">
          <p className="text-sm font-medium leading-tight">{name || "Account"}</p>
          {email && (
            <p className="mt-0.5 truncate text-xs text-muted-foreground">
              {email}
            </p>
          )}
        </div>

        {primaryActions.map((action) => (
          <DropdownMenuItem
            key={action.label}
            onClick={action.onSelect}
            className="cursor-pointer gap-2 py-2 hover:bg-muted/70 hover:text-foreground focus:bg-muted/70 focus:text-foreground"
          >
            <action.icon className="size-4 shrink-0" />
            <span>{action.label}</span>
          </DropdownMenuItem>
        ))}

        {sections?.map((section, index) => (
          <div key={section.label ?? index}>
            <DropdownMenuSeparator className="my-1" />
            {section.label && (
              <p className="mb-1 px-4 text-xs font-semibold text-muted-foreground">
                {section.label}
              </p>
            )}
            {section.items.map((item) => (
              <DropdownMenuItem
                key={item.label}
                onClick={item.onSelect}
                className="cursor-pointer gap-2 py-2 hover:bg-muted/70 hover:text-foreground focus:bg-muted/70 focus:text-foreground"
              >
                <item.icon className="size-4 shrink-0" />
                <span>{item.label}</span>
              </DropdownMenuItem>
            ))}
          </div>
        ))}

        {/* Portal Switcher - shows all portals with active indicator */}
        {/* Positioned between Settings and Logout */}
        <PortalSwitcherMenu />

        <DropdownMenuSeparator className="my-1" />

        <DropdownMenuItem
          onClick={onSignOut}
          className="cursor-pointer gap-2 py-2 text-destructive focus:bg-destructive/10"
        >
          <LogOut className="size-4 shrink-0" />
          <span>{signOutLabel}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
